import { z } from "zod";
export const VERSION = "0.4.0";
export const laneKeys = [
  "backlog",
  "ready",
  "in_progress",
  "wait_user",
  "done",
] as const;
export const defaultLanes = laneKeys.map((key, position) => ({
  key,
  title: ["Backlog", "Ready", "In progress", "Wait-user", "Done"][position],
  position,
  system: true,
  clock: !["backlog", "done"].includes(key),
  collapsed: false,
}));
const text = z.string().trim().min(1).max(10000);
export const taskId = z.string().regex(/^[A-Z]+-\d+$/);
export const checklistSchema = z
  .array(
    z.object({
      text: text.max(1000),
      state: z.enum(["todo", "current", "done"]),
    }),
  )
  .max(100);
export const questionSchema = z.object({
  text,
  suggestions: z
    .array(
      z.object({
        label: text.max(500),
        why: z.string().max(2000).default(""),
        recommended: z.boolean().default(false),
      }),
    )
    .max(10)
    .default([]),
});
export const sessionSchema = z.object({
  machine: text.max(100),
  harness: text.max(100),
  model: z.string().max(100).optional(),
  harness_session_id: text.max(200).optional(),
  client: z.string().max(100).optional(),
});
export const agentIdentitySchema = z.object({
  name: z
    .string()
    .trim()
    .min(1)
    .max(100)
    .regex(/^[a-zA-Z0-9][a-zA-Z0-9._-]*$/),
  machine: text.max(100).default("http-client"),
  harness: text.max(100).default("http"),
  instructions: z.string().max(20000).default(""),
});
export const editAgentIdentitySchema = agentIdentitySchema
  .partial()
  .extend({ enabled: z.boolean().optional() });
export const identitySessionSchema = sessionSchema
  .partial()
  .extend({ identity: z.string().max(100).optional() });
export type AgentIdentityInput = z.infer<typeof agentIdentitySchema>;
export type AgentIdentityEdit = z.infer<typeof editAgentIdentitySchema>;
export interface AgentIdentity extends AgentIdentityInput {
  id: string;
  enabled: boolean;
  token_suffix: string;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}
export const taskFields = {
  title: text.max(300),
  description: z.string().max(20000),
  priority: z.enum(["urgent", "high", "normal", "low"]),
  tags: z.array(text.max(50)).max(30),
  criteria: z
    .array(z.object({ text: text.max(2000), met: z.boolean().default(false) }))
    .max(100),
  branch: z.string().max(300),
  parent: taskId.nullable(),
  relations: z
    .array(z.object({ kind: z.enum(["blocks", "relates_to"]), task: taskId }))
    .max(100),
};
export const createTaskSchema = z.object({
  ...taskFields,
  description: taskFields.description.default(""),
  priority: taskFields.priority.default("normal"),
  tags: taskFields.tags.default([]),
  criteria: taskFields.criteria.default([]),
  branch: taskFields.branch.default(""),
  parent: taskFields.parent.default(null),
  relations: taskFields.relations.default([]),
});
export const parentSearchSchema = z.object({
  q: z.string().trim().max(300).default(""),
  offset: z.coerce.number().int().nonnegative().default(0),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ParentCandidate = { id: string; title: string; tags: string[] };
export type ParentSearch = {
  items: ParentCandidate[];
  total: number;
  next_offset: number | null;
};
export const editTaskSchema = z.object(taskFields).partial();
export const runSchema = z.object({
  task: taskId,
  instruction: text,
  mode: z.enum(["read", "edit"]).default("read"),
  checkout: z.string().trim().min(1).max(1000).optional(),
  include: z
    .object({
      activity: z.number().int().min(1).max(1000).optional(),
      runs: z
        .union([z.number().int().min(1).max(1000), z.literal("all")])
        .optional(),
    })
    .optional(),
});
export const endSchema = z.object({
  outcome: z
    .enum(["handed_off", "blocked", "failed", "abandoned"])
    .default("handed_off"),
  handoff: z.object({
    done: text.max(20000),
    next: text.max(20000),
    blocked: z.string().max(10000).default(""),
  }),
  checklist: checklistSchema.optional(),
  move_to: text.max(50).optional(),
  questions: z.array(questionSchema).max(30).default([]),
  usage: z
    .object({
      tokens_in: z.number().int().nonnegative().optional(),
      tokens_out: z.number().int().nonnegative().optional(),
      usd: z.number().nonnegative().optional(),
    })
    .optional(),
  transcript_ref: z.string().max(2000).optional(),
});
export const commentSchema = z.object({
  body: text.max(50000),
  attachment_ids: z.array(z.string()).max(30).default([]),
});
export const contextSchema = z.object({
  kind: z.enum(["decision", "gotcha", "failed", "file", "text"]),
  title: z.string().max(500).default(""),
  body: text.max(50000),
  why: z.string().max(3000).default(""),
  attachment_ids: z.array(z.string()).max(30).default([]),
});
export const moveSchema = z.object({
  lane: text.max(50),
  comment: z.string().max(10000).optional(),
});
export const answerSchema = z.object({
  answers: z
    .array(
      z.object({
        question_id: z.string(),
        text,
        suggestion_idx: z.number().int().nonnegative().optional(),
      }),
    )
    .min(1)
    .max(30),
});
export const projectSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]{1,60}$/),
  name: text.max(100),
  prefix: z.string().regex(/^[A-Z]{1,12}$/),
  base_branch: text.max(100).default("main"),
});
export const settingsSchema = z
  .object({
    guidance: z.string().max(50000).default(""),
    ready_check: z
      .array(text.max(1000))
      .max(50)
      .default([
        "Clear problem and expected behavior",
        "Acceptance criteria",
        "Relevant context and affected files",
      ]),
    lane_templates: z.record(z.string(), z.string().max(1000)).default({
      backlog: "triage {id}",
      ready: "build {id}",
      in_progress: "continue {id}",
      wait_user: "review {id}",
      done: "",
    }),
    machines: z
      .record(
        z.string(),
        z.object({
          repo_path: z.string().max(1000),
          tools: z.string().max(2000).default(""),
          notes: z.string().max(5000).default(""),
        }),
      )
      .default({}),
    stale_after_minutes: z.number().int().min(1).max(1440).default(15),
    session_idle_timeout_minutes: z
      .number()
      .int()
      .min(2)
      .max(10080)
      .default(120),
    attachment_max_mb: z.number().int().min(1).max(100).default(25),
    briefing_budget: z.number().int().min(4000).max(100000).default(24000),
    archive_days: z.number().int().min(0).max(3650).default(90),
    show_spend: z.boolean().default(true),
    warn_usd: z.number().nonnegative().default(0),
    warn_tokens: z.number().int().nonnegative().default(0),
  })
  .refine((s) => s.stale_after_minutes < s.session_idle_timeout_minutes, {
    message: "Stale threshold must be shorter than idle timeout",
  });
export const lanesSchema = z
  .array(
    z.object({
      key: z.string().regex(/^[a-z][a-z0-9_]{0,49}$/),
      title: text.max(60),
      clock: z.boolean(),
      collapsed: z.boolean().default(false),
    }),
  )
  .min(5)
  .max(20);
export const attachmentSchema = z.object({
  task: taskId,
  filename: text.max(255),
  mime: z.string().max(150).default("application/octet-stream"),
  base64: z.string().max(15_000_000),
  caption: z.string().max(2000).default(""),
  comment_id: z.string().optional(),
  context_id: z.string().optional(),
});
export type Settings = z.infer<typeof settingsSchema>;
export type TaskInput = z.infer<typeof createTaskSchema>;
export type RunInput = z.infer<typeof runSchema>;
export type EndInput = z.infer<typeof endSchema>;
export type Checklist = z.infer<typeof checklistSchema>;
export type Actor =
  | { human: true; name: string }
  | { human: false; name: string; session_id: string };
export class DomainError extends Error {
  constructor(
    public code: string,
    message: string,
    public status = 400,
    public hint = "",
  ) {
    super(message);
  }
}
export function fail(
  code: string,
  message: string,
  status = 400,
  hint = "",
): never {
  throw new DomainError(code, message, status, hint);
}
export function requireHuman(actor: Actor) {
  if (!actor.human)
    fail(
      "FORBIDDEN",
      "This action needs your admin login.",
      403,
      "Sign in to the web UI.",
    );
}
export function needsHuman(lane: string, unanswered: number) {
  return lane === "wait_user" || unanswered > 0;
}
export function normalizeCheckout(machine: string, path: string) {
  const canonical = path.replaceAll("\\", "/");
  if (!canonical.startsWith("/") && !/^[a-z]:\//i.test(canonical))
    fail(
      "VALIDATION",
      "Checkout must be an absolute canonical path, not a relative path or ~ alias.",
    );
  const segments: string[] = [];
  for (const segment of canonical.split("/")) {
    if (!segment || segment === ".") continue;
    if (segment === "..") segments.pop();
    else segments.push(segment);
  }
  let normalized =
    (canonical.startsWith("//") ? "//" : canonical.startsWith("/") ? "/" : "") +
    segments.join("/");
  if (/^[a-z]:/i.test(normalized)) normalized = normalized.toLowerCase();
  return `${machine.toLowerCase()}::${normalized}`;
}
export function allowedMoves(lanes: string[], human: boolean) {
  return lanes.filter((l) => human || l !== "done");
}
export {
  attachmentPreviewKind,
  imagePreviewTypes,
  videoPreviewTypes,
  audioPreviewTypes,
} from "./attachments.js";
